import React, { useState, useEffect } from 'react';
import { Save, ArrowRight, ArrowLeftRight } from 'lucide-react';
import Select from 'react-select';
import { Checkbox, Autocomplete } from '@anyemp/ui-kit';
import { statuses as statusService, users as userService } from '../../services/apiService';
import { getTalentTypeLabel, getTalentTypeColor, getStatusTypes, statusesShareStage } from '../../utils/talentTypeUtils';

// Подпись статуса в выпадающем списке: имя и стадии, на которых он доступен
const statusOptionLabel = (status) => {
  const stageNames = (status.stages || []).map(stage => stage.name);
  if (stageNames.length > 0) return `${status.name} (${stageNames.join(', ')})`;
  return `${status.name} (${status.talent_type === 'talent' ? 'no stages' : getTalentTypeLabel(status.talent_type)})`;
};

// Переход возможен между статусами одной сущности; для статусов талантов —
// только если у них есть общая стадия: смена статуса стадию не двигает
const canTransitionBetween = (from, to) => {
  if (from.talent_type !== to.talent_type) return false;
  return from.talent_type !== 'talent' || statusesShareStage(from, to);
};

const StatusTypeBadges = ({ status }) => (
  <>
    {getStatusTypes(status).map((type) => (
      <span key={type} className={`text-xs px-2 py-1 rounded-full ${getTalentTypeColor(type)}`}>
        {getTalentTypeLabel(type)}
      </span>
    ))}
  </>
);

const TransitionForm = ({ 
  mode = 'create', 
  initialData = null, 
  fromStatusId = null,
  onSubmit, 
  onCancel 
}) => {
  // Styles for react-select
  const selectStyles = {
    control: (base, { isFocused }) => ({
      ...base,
      backgroundColor: 'white',
      borderColor: isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e0e7ff'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#3730a3'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#6366f1',
      '&:hover': {
        backgroundColor: '#a5b4fc',
        color: '#fff'
      }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999
    })
  };

  const darkSelectStyles = {
    ...selectStyles,
    control: (base, { isFocused }) => ({
      ...base,
      backgroundColor: '#374151',
      borderColor: isFocused ? '#3b82f6' : '#4b5563',
      color: '#fff',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#374151',
      zIndex: 9999
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected ? '#3b82f6' : isFocused ? '#4b5563' : '#374151',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#4b5563'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#1e40af'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#fff'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#cbd5e1',
      '&:hover': {
        backgroundColor: '#dc2626',
        color: '#fff'
      }
    })
  };
  const [formData, setFormData] = useState({
    from_status_id: fromStatusId || initialData?.from_status?.id || '',
    to_status_id: initialData?.to_status?.id || '',
    is_bidirectional: initialData?.is_bidirectional || false,
    requires_approval: initialData?.requires_approval || false,
    approval_role: Array.isArray(initialData?.approval_role) ? initialData.approval_role : (initialData?.approval_role ? [initialData.approval_role] : []),
    transition_reason_required: initialData?.transition_reason_required !== false
  });

  const [statuses, setStatuses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Determine active styles for select
  const getSelectStyles = () => {
    // Check if dark class exists on body or html element
    const isDarkMode = document.documentElement.classList.contains('dark') || 
                      document.body.classList.contains('dark') ||
                      window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDarkMode ? darkSelectStyles : selectStyles;
  };

  useEffect(() => {
    loadStatuses();
    loadRoles();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        from_status_id: initialData.from_status?.id || '',
        to_status_id: initialData.to_status?.id || '',
        is_bidirectional: initialData.is_bidirectional || false,
        requires_approval: initialData.requires_approval || false,
        approval_role: Array.isArray(initialData.approval_role) ? initialData.approval_role : (initialData.approval_role ? [initialData.approval_role] : []),
        transition_reason_required: initialData.transition_reason_required !== false
      });
    }
  }, [initialData]);

  const loadStatuses = async () => {
    try {
      const response = await statusService.getAllStatuses({
        active_only: 'true',
        all: true
      });
      setStatuses(response.data);
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await userService.getRoles();
      if (response.success) {
        setRoles(response.data || []);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
      // Do not show error to user as there are fallback roles
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.from_status_id) {
      newErrors.from_status_id = 'Select source status';
    }

    if (!formData.to_status_id) {
      newErrors.to_status_id = 'Select target status';
    }

    if (formData.from_status_id && formData.to_status_id &&
        formData.from_status_id === formData.to_status_id) {
      newErrors.to_status_id = 'Source and target statuses cannot be the same';
    }

    if (formData.requires_approval && (!formData.approval_role || formData.approval_role.length === 0)) {
      newErrors.approval_role = 'Select at least one role for approval';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFromStatus = () => {
    return statuses.find(s => s.id === parseInt(formData.from_status_id));
  };

  const getToStatus = () => {
    return statuses.find(s => s.id === parseInt(formData.to_status_id));
  };

  const fromStatus = getFromStatus();
  const toStatus = getToStatus();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Status Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* From Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Source Status
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            The status from which the transition will be performed
          </p>
          <Autocomplete
            clearable
            fullWidth
            options={statuses.map(status => ({
              value: String(status.id),
              label: statusOptionLabel(status)
            }))}
            value={formData.from_status_id != null ? String(formData.from_status_id) : ''}
            onChange={(value) => handleInputChange('from_status_id', value ? Number(value) : '')}
            placeholder="Select status"
            disabled={mode === 'edit' || fromStatusId}
            error={!!errors.from_status_id}
          />
          {errors.from_status_id && (
            <p className="mt-1 text-sm text-red-600">{errors.from_status_id}</p>
          )}
          {fromStatus && (
            <div className="mt-2 flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full border border-gray-300"
                style={{ backgroundColor: fromStatus.color || '#6B7280' }}
              ></div>
              <StatusTypeBadges status={fromStatus} />
            </div>
          )}
        </div>

        {/* To Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Target Status
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            The status to which the transition will be performed
          </p>
          {fromStatus && (
            <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-700 dark:text-blue-400">
              ℹ️ Only statuses that share a stage with <strong>{fromStatus.name}</strong> are available:
              changing the status never moves the talent to another stage. To move between stages
              (and talent types), change the stage on the talent page.
            </div>
          )}
          <Autocomplete
            clearable
            fullWidth
            options={statuses
              .filter(status => status.id !== parseInt(formData.from_status_id))
              .filter(status => {
                // If there is a source status, show only statuses it can transition to
                if (fromStatus) {
                  return canTransitionBetween(fromStatus, status);
                }
                return true;
              })
              .map(status => ({
                value: String(status.id),
                label: statusOptionLabel(status)
              }))}
            value={formData.to_status_id != null ? String(formData.to_status_id) : ''}
            onChange={(value) => handleInputChange('to_status_id', value ? Number(value) : '')}
            placeholder="Select status"
            error={!!errors.to_status_id}
          />
          {errors.to_status_id && (
            <p className="mt-1 text-sm text-red-600">{errors.to_status_id}</p>
          )}
          {toStatus && (
            <div className="mt-2 flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full border border-gray-300"
                style={{ backgroundColor: toStatus.color || '#6B7280' }}
              ></div>
              <StatusTypeBadges status={toStatus} />
            </div>
          )}
        </div>
      </div>

      {/* Visual Preview */}
      {fromStatus && toStatus && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="text-center mb-3">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</h5>
            <p className="text-xs text-gray-500 dark:text-gray-400">How the transition will look</p>
          </div>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full border border-gray-300"
                style={{ backgroundColor: fromStatus.color || '#6B7280' }}
              ></div>
              <span className="font-medium text-gray-900 dark:text-white">
                {fromStatus.name}
              </span>
            </div>

            <div className="flex items-center gap-1">
              {formData.is_bidirectional ? (
                <ArrowLeftRight className="w-5 h-5 text-gray-500" />
              ) : (
                <ArrowRight className="w-5 h-5 text-gray-500" />
              )}
            </div>

            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-full border border-gray-300"
                style={{ backgroundColor: toStatus.color || '#6B7280' }}
              ></div>
              <span className="font-medium text-gray-900 dark:text-white">
                {toStatus.name}
              </span>
            </div>
          </div>

          {/* Properties indicators */}
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            {formData.requires_approval && (
              <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                🔒 Requires approval ({Array.isArray(formData.approval_role) ? formData.approval_role.join(', ') : formData.approval_role})
              </span>
            )}
            {formData.transition_reason_required && (
              <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                📝 Requires reason
              </span>
            )}
            {formData.is_bidirectional && (
              <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                ↔ Bidirectional
              </span>
            )}
          </div>
        </div>
      )}

      {/* Transition Properties */}
      <div className="space-y-4">
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Transition Properties
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Configure additional transition parameters between statuses
          </p>
        </div>

        {/* Bidirectional */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-2">
          <Checkbox
            checked={formData.is_bidirectional}
            onChange={(checked) => handleInputChange('is_bidirectional', checked)}
            label="Bidirectional Transition"
            color="primary"
          />
          <div className="ml-7">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>What it is:</strong> If enabled, the transition works in both directions: A → B and B → A
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Examples:</strong>
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">
                ✅ <strong>Bidirectional:</strong> "Active" ↔ "On Vacation" (can transfer back and forth)
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">
                ❌ <strong>Unidirectional:</strong> "New Candidate" → "In Interview" (cannot go back)
              </p>
            </div>
          </div>
        </div>

        {/* Requires Approval */}
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 space-y-2">
          <Checkbox
            checked={formData.requires_approval}
            onChange={(checked) => handleInputChange('requires_approval', checked)}
            label="Requires Approval"
            color="warning"
          />
          <div className="ml-7">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>What it is:</strong> If enabled, the transition can only be performed after approval from the specified role
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Examples:</strong>
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                🔒 <strong>With approval:</strong> "In Interview" → "Hired" (requires manager approval)
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">
                ✅ <strong>Without approval:</strong> "New Candidate" → "In Interview" (HR can transfer immediately)
              </p>
            </div>
          </div>
        </div>

        {/* Approval Role */}
        {formData.requires_approval && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Approval Roles
            </label>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              <strong>What it is:</strong> Specifies the user roles that can approve this transition. Use search to quickly find the desired role or select multiple roles.
            </p>
            <Select
              isMulti
              options={roles}
              value={roles.filter(role => formData.approval_role.includes(role.value))}
              onChange={(selectedOptions) => {
                const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
                handleInputChange('approval_role', values);
              }}
              placeholder="Enter role name or select from the list..."
              noOptionsMessage={() => "Roles not found or not loaded"}
              styles={getSelectStyles()}
              isClearable
              isSearchable
              maxMenuHeight={200}
              closeMenuOnSelect={false}
              classNamePrefix="role-select"
              className={`text-sm ${errors.approval_role ? 'border-red-300' : ''}`}
              theme={(theme) => ({
                ...theme,
                colors: {
                  ...theme.colors,
                  primary: '#3b82f6',
                  primary75: '#60a5fa',
                  primary50: '#93c5fd',
                  primary25: '#dbeafe',
                },
              })}
            />
            <div className="mt-2 space-y-1">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Selected roles:</strong>
              </p>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formData.approval_role.length > 0 ? (
                  <p>Selected {formData.approval_role.length} roles: {formData.approval_role.join(', ')}</p>
                ) : (
                  <p>No roles selected</p>
                )}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                Total available: {roles.length} roles
              </div>
            </div>
            {errors.approval_role && (
              <p className="mt-1 text-sm text-red-600">{errors.approval_role}</p>
            )}
          </div>
        )}

        {/* Reason Required */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 space-y-2">
          <Checkbox
            checked={formData.transition_reason_required}
            onChange={(checked) => handleInputChange('transition_reason_required', checked)}
            label="Requires Reason"
            color="success"
          />
          <div className="ml-7">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>What it is:</strong> If enabled, a reason must be specified when performing the transition
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Examples:</strong>
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">
                📝 <strong>Requires reason:</strong> "Active Employee" → "Fired" (must specify the reason for dismissal)
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                ✅ <strong>Does not require reason:</strong> "New Candidate" → "In Interview" (can transfer without reason)
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                <em>The reason is saved in the change history for audit purposes</em>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          {mode === 'create' ? 'Create' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default TransitionForm;
